// This plugin's own state: the account to import for, which of its months
// are in, and where an import stands. The month list and every fetched
// response live in the plugin's website-keyed folder,
// `archives/chesscom/<account>/`, and nowhere near the games API. Nothing
// here calls chess.com except the import, from a click or from the daily
// sync (`@chess/api-sync`, which index.tsx provides over this store). The
// import covers the last 30 days only, the app's one window
// (`@chess/api-games/filter`): the months that hold them, and of their games
// only those played in the window. When it completes, the local day is
// written to the bookkeeping (`lastSyncDay`): the daily sync is due while
// that is not today.
import { create } from 'zustand';
import type { Storage } from '@chess/api-storage';
import type { Games } from '@chess/api-games';
import { inPeriod, periodStart } from '@chess/api-model';
import { localDay } from '@chess/api-sync';
import type { Toast } from '@chess/api-toast';
import { monthOf, toGameRecord } from './chesscom.ts';
import {
  ArchiveStore, IMPORT_CLICK_INTERVAL_MS, LIVE_MONTH_INTERVAL_MS, ageMs, consoleLog, currentMonth, loadArchives, loadMonth, monthsSince, readUsername,
  type CallLog,
} from './import.ts';

export type Status = 'loading' | 'idle' | 'importing' | 'done' | 'error';

export const SETTINGS_FILE = 'chesscom.json';

/** The storage folder every path of this importer lives under. */
export const ARCHIVES_FOLDER = 'archives/chesscom';

export interface ImportProgress {
  /** The month being read, "2026/10". */
  month: string;
  imported: number;
  skipped: number;
}

export interface Deps {
  games: Games;
  /** The importer's website-keyed folder, `archives/chesscom`. */
  storage: Storage;
  fetch?: typeof fetch;
  now?: () => Date;
  log?: CallLog;
  /** Where a refused click is explained; optional so the store runs without a UI. */
  toast?: Toast;
}

interface State {
  /** '' when no account is configured. */
  username: string;
  status: Status;
  progress?: ImportProgress;
  error?: string;
  /** The stored archive list, oldest first; undefined until chess.com has been asked once. */
  archives?: string[];
  /** Archive URLs whose games are in the library. */
  imported: string[];
  /** A sentence for a `done` that imported nothing. */
  note?: string;
  /** The local day the last import completed, from the bookkeeping; undefined when none ever did for this account. */
  lastSyncDay?: string;
  load(settings: Storage, archives: Storage): Promise<void>;
  /** The import. `auto` is the daily sync's: not a click, so the one-click-a-minute rule does not apply. */
  importRecent(deps: Deps, options?: { auto?: boolean }): Promise<void>;
}

export const useImport = create<State>((set, get) => {
  // One import at a time: `status` is the lock, and each request inside is awaited in turn.
  async function run(deps: Deps, auto: boolean): Promise<void> {
    const { username, status } = get();
    if (!username || status === 'importing') return;
    const fetchFn = deps.fetch ?? fetch;
    const log = deps.log ?? consoleLog;
    const today = (deps.now ?? (() => new Date()))();
    const now = today.getTime();
    const store = new ArchiveStore(deps.storage, username);
    // A click is refused within a minute of the last, and the click time is
    // kept with the rest of the bookkeeping so a reload does not reset it.
    // The user is told how long is left, in a toast. The daily sync is not a
    // click: the day rule governs it, so a reload right after a click still
    // syncs a new day.
    const before = await store.readState();
    const remaining = IMPORT_CLICK_INTERVAL_MS - ageMs(before.importClickedAt, today);
    if (!auto && remaining > 0) {
      deps.toast?.show(importWaitMessage(remaining), { kind: 'warning' });
      return;
    }
    await store.writeState({ ...before, importClickedAt: auto ? before.importClickedAt : today.toISOString() });
    /** The import completed: today is synced, in the bookkeeping and in the state. */
    const synced = async (): Promise<Pick<State, 'imported' | 'lastSyncDay'>> => {
      const state = await store.readState();
      const lastSyncDay = localDay(today);
      await store.writeState({ ...state, lastSyncDay });
      return { imported: state.months, lastSyncDay };
    };
    set({ status: 'importing', progress: undefined, error: undefined, note: undefined });
    try {
      const archives = await loadArchives({ store, fetch: fetchFn, username, now: today, log, mayFetch: true });
      const months = archives ? monthsSince(archives, periodStart('30d', now)) : [];
      if (!months.length) {
        set({ status: 'done', archives, ...(await synced()), note: 'No games on chess.com in the last 30 days.' });
        return;
      }
      const progress: ImportProgress = { month: monthOf(months[0]), imported: 0, skipped: 0 };
      const importedAt = today.toISOString();
      for (const month of months) {
        const state = await store.readState();
        const closed = state.closedMonths ?? [];
        // The live month can still grow: fetched again at most once a minute.
        // A past month is fetched once after it ended (a copy stored while it
        // was live may miss its last days), then served from storage for good.
        const live = monthOf(month) === currentMonth(today);
        const fresh = live ? ageMs(state.liveMonthFetchedAt, today) >= LIVE_MONTH_INTERVAL_MS : !closed.includes(month);
        const games = await loadMonth({ store, fetch: fetchFn, url: month, log, fresh });
        const after = await store.readState();
        await store.writeState({
          ...after,
          liveMonthFetchedAt: live && fresh ? today.toISOString() : after.liveMonthFetchedAt,
          closedMonths: !live && !closed.includes(month) ? [...closed, month] : after.closedMonths,
          months: after.months.includes(month) ? after.months : [...after.months, month],
        });
        progress.month = monthOf(month);
        set({ progress: { ...progress } });
        for (const game of games) {
          // Only the window's games: nothing older than 30 days comes in.
          if (!game.end_time || !inPeriod(game.end_time * 1000, '30d', now)) continue;
          const record = toGameRecord(game, username, importedAt);
          if (await deps.games.get(record.id)) progress.skipped++;
          else {
            await deps.games.put(record);
            progress.imported++;
          }
          set({ progress: { ...progress } });
        }
      }
      set({ status: 'done', archives, ...(await synced()) });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  }

  return {
    username: '',
    status: 'loading',
    imported: [],
    // Reads storage only; nothing is fetched on load.
    async load(settings, archives) {
      const username = readUsername(await settings.readText(SETTINGS_FILE));
      const store = username ? new ArchiveStore(archives, username) : undefined;
      const state = store ? await store.readState() : undefined;
      set({
        username,
        archives: store ? await store.readArchives() : undefined,
        imported: state?.months ?? [],
        lastSyncDay: state?.lastSyncDay,
        status: get().status === 'loading' ? 'idle' : get().status,
      });
    },
    importRecent: (deps, options) => run(deps, !!options?.auto),
  };
});

/** Whether a button may be pressed now: an account is set and nothing is running. */
export function canImport(state: Pick<State, 'username' | 'status'>): boolean {
  return !!state.username && state.status !== 'loading' && state.status !== 'importing';
}

/** The one-line status beside the button. */
export function statusText(state: Pick<State, 'username' | 'status' | 'progress' | 'error' | 'note'>): string {
  const p = state.progress;
  switch (state.status) {
    case 'loading':
      return '';
    case 'idle':
      // The button says what it does; the line speaks only when there is news.
      return state.username ? '' : 'Set your chess.com username in Settings to import.';
    case 'importing':
      return p ? `Importing the last 30 days, ${p.imported} games so far.` : 'Starting import.';
    case 'done': {
      if (state.note) return state.note;
      const n = p?.imported ?? 0;
      const skipped = p?.skipped ? `, ${p.skipped} already in the library` : '';
      return `Imported ${n} ${n === 1 ? 'game' : 'games'} from the last 30 days${skipped}.`;
    }
    case 'error':
      return `Import failed: ${state.error ?? 'unknown error'}`;
  }
}

/** "Import available again in 42 s", for a click refused inside the minute. */
export function importWaitMessage(remainingMs: number): string {
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return `Import available again in ${seconds} s`;
}
